import React from 'react';
import { Badge, Card } from 'react-bootstrap';
import { formatLongDate } from './shared';
import ResumeAnswer from './ResumeAnswer';
import { LA_QUESTIONS, PRIOR_ROLES, otherRoleInterestText } from './laAnswers';
import './programs.css';

const yesNo = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'Not answered';
};

function Answer({ question, children }) {
    return (
        <>
            <dt className="small text-muted fw-normal">{question}</dt>
            <dd className="mb-3">{children}</dd>
        </>
    );
}

// Read-only view of the Learning Assistant answers. `application` is the
// server's shape (or one built from the wizard's answers). Every reader sees
// the same answers: the server decides which fields it sends to each. The
// resume downloads as ResumeAnswer describes.
function LaSummary({ application, fetchResume }) {
    const details = application.laDetails || {};
    const commitments = application.commitments;
    const roles = (details.priorRoles || []).map(
        (value) =>
            PRIOR_ROLES.find((role) => role.value === value)?.label || value
    );

    return (
        <Card className="mb-3 border-0 shadow-sm">
            <Card.Header className="bg-light">
                <strong>LA Details</strong>
            </Card.Header>
            <Card.Body>
                <dl className="mb-0">
                    <Answer question={LA_QUESTIONS.isReturning}>
                        {yesNo(details.isReturning)}
                        {typeof details.isReturning === 'boolean' && (
                            <>
                                {' '}
                                <Badge bg="secondary">
                                    {details.isReturning
                                        ? 'Returning LA'
                                        : 'New LA'}
                                </Badge>
                            </>
                        )}
                    </Answer>
                    {details.isReturning === false && (
                        <>
                            <Answer
                                question={LA_QUESTIONS.canAttendOrientation}
                            >
                                {yesNo(details.canAttendOrientation)}
                                {details.canAttendOrientation === false && (
                                    <>
                                        {' '}
                                        <Badge bg="warning" text="dark">
                                            Cannot attend orientation
                                        </Badge>
                                    </>
                                )}
                            </Answer>
                            <Answer question={LA_QUESTIONS.canAttendPedagogy}>
                                {yesNo(details.canAttendPedagogy)}
                                {details.canAttendPedagogy === false && (
                                    <>
                                        {' '}
                                        <Badge bg="warning" text="dark">
                                            Cannot attend the pedagogy seminar
                                        </Badge>
                                    </>
                                )}
                            </Answer>
                        </>
                    )}
                    <Answer question={LA_QUESTIONS.interestStatement}>
                        <span className="program-text-pre-line">
                            {application.interestStatement || 'Not answered'}
                        </span>
                    </Answer>
                    <Answer question={LA_QUESTIONS.priorExperience}>
                        <span className="program-text-pre-line">
                            {application.priorExperience || 'Not answered'}
                        </span>
                    </Answer>
                    <Answer question={LA_QUESTIONS.priorRoles}>
                        {roles.length ? roles.join(', ') : 'Not answered'}
                    </Answer>
                    <Answer question={LA_QUESTIONS.otherRoleInterest}>
                        {otherRoleInterestText(details.otherRoleInterest)}
                    </Answer>
                    <Answer question="Resume">
                        <ResumeAnswer
                            application={application}
                            fetchResume={fetchResume}
                        />
                    </Answer>
                </dl>
                <p className="mb-0">
                    {commitments?.acknowledgedAt
                        ? `Commitments acknowledged on ${formatLongDate(commitments.acknowledgedAt)}`
                        : commitments?.acknowledged
                          ? 'Commitments acknowledged (the date is recorded when you submit)'
                          : 'Commitments not yet acknowledged'}
                </p>
                {commitments?.statements?.length > 0 && (
                    <ol className="mt-2 mb-0">
                        {commitments.statements.map((text) => (
                            <li key={text}>{text}</li>
                        ))}
                    </ol>
                )}
            </Card.Body>
        </Card>
    );
}

export default LaSummary;
